/**
 * 项目设定自动生成相关类型定义
 * 
 * 用于"一键创建剧本"功能，支持自动生成项目的所有设定
 * 包括：项目信息、故事梗概、世界观、写作风格、分集大纲、艺术风格、主体设定
 * 
 * @module projectSettingGeneration
 * @see Requirements 4.1-4.4, 5.1
 */

/**
 * 生成步骤类型枚举
 * 
 * 定义自动生成流程中的所有步骤，按执行顺序排列：
 * 1. PROJECT_META - 项目标题和描述（第一步）
 * 2. STORY_SYNOPSIS - 故事梗概
 * 3. WORLDVIEW - 世界观
 * 4. WRITING_STYLE - 写作风格
 * 5. EPISODE_OUTLINE - 分集大纲
 * 6. ART_STYLE - 艺术风格
 * 7. SUBJECT - 主体设定
 */
export enum GenerationStepType {
  /** 项目标题和描述（第一步，生成后更新 Project 表） */
  PROJECT_META = 'project_meta',
  /** 故事梗概 */
  STORY_SYNOPSIS = 'story_synopsis',
  /** 世界观 */
  WORLDVIEW = 'worldview',
  /** 写作风格 */
  WRITING_STYLE = 'writing_style',
  /** 分集大纲 */
  EPISODE_OUTLINE = 'episode_outline',
  /** 艺术风格 */
  ART_STYLE = 'art_style',
  /** 主体设定 */
  SUBJECT = 'subject',
  /** 小说解析（小说流程专用） */
  NOVEL_PARSE = 'novel_parse',
  /** 小说智能切分（小说流程专用 - 将小说切分为分集剧本） */
  NOVEL_SPLIT = 'novel_split',
  /** 分集剧本（第7步，查看和编辑剧本） */
  EPISODE_SCRIPT = 'episode_script',
}

/**
 * 项目设定生成步骤状态枚举
 * 
 * 每个步骤在任意时刻只能处于以下状态之一
 * 注意：此枚举用于项目设定自动生成，与 episodeScript.ts 中的 GenerationStepStatus 接口不同
 * @see Requirements 5.1
 */
export enum ProjectSettingStepStatus {
  /** 等待中，尚未开始 */
  PENDING = 'pending',
  /** 正在生成中 */
  IN_PROGRESS = 'in_progress',
  /** 生成完成 */
  COMPLETED = 'completed',
  /** 生成失败 */
  FAILED = 'failed'
}

// ============================================================================
// WebSocket 消息类型接口
// ============================================================================

/**
 * 步骤开始消息
 * 
 * 当一个生成步骤开始时，通过 WebSocket 广播此消息
 * @see Requirements 4.1
 */
export interface ProjectSettingStepStartedMessage {
  /** 消息类型标识 */
  messageType: 'project_setting:step_started';
  /** 项目 ID */
  projectId: string;
  /** 步骤类型 */
  stepType: GenerationStepType;
  /** 步骤索引（0-6） */
  stepIndex: number;
  /** 用户 ID */
  userId: string;
  /** 时间戳 (ISO 8601 格式) */
  timestamp: string;
}

/**
 * 步骤进度消息（流式内容）
 * 
 * 在生成过程中，实时推送流式内容更新
 * @see Requirements 4.2
 */
export interface ProjectSettingStepProgressMessage {
  /** 消息类型标识 */
  messageType: 'project_setting:step_progress';
  /** 项目 ID */
  projectId: string;
  /** 步骤类型 */
  stepType: GenerationStepType;
  /** 本次增量内容 */
  delta: string;
  /** 完整内容（累积） */
  fullContent: string;
  /** 是否已完成流式输出 */
  isComplete: boolean;
  /** 用户 ID */
  userId: string;
  /** 时间戳 (ISO 8601 格式) */
  timestamp: string;
}

/**
 * 步骤完成消息
 * 
 * 当一个生成步骤成功完成时，通过 WebSocket 广播此消息
 * @see Requirements 4.3
 */
export interface ProjectSettingStepCompletedMessage {
  /** 消息类型标识 */
  messageType: 'project_setting:step_completed';
  /** 项目 ID */
  projectId: string;
  /** 步骤类型 */
  stepType: GenerationStepType;
  /** 生成的完整内容 */
  content: string;
  /** 用户 ID */
  userId: string;
  /** 时间戳 (ISO 8601 格式) */
  timestamp: string;
}

/**
 * 步骤失败消息
 * 
 * 当一个生成步骤失败时，通过 WebSocket 广播此消息
 * @see Requirements 4.4
 */
export interface ProjectSettingStepFailedMessage {
  /** 消息类型标识 */
  messageType: 'project_setting:step_failed';
  /** 项目 ID */
  projectId: string;
  /** 步骤类型 */
  stepType: GenerationStepType;
  /** 错误信息 */
  error: string;
  /** 用户 ID */
  userId: string;
  /** 时间戳 (ISO 8601 格式) */
  timestamp: string;
}

/**
 * 项目信息更新消息
 * 
 * PROJECT_META 步骤专用消息，当项目标题和描述生成完成后广播
 * 前端收到此消息后应更新项目信息显示
 * @see Requirements 2.7
 */
export interface ProjectMetaUpdatedMessage {
  /** 消息类型标识 */
  messageType: 'project_setting:project_meta_updated';
  /** 项目 ID */
  projectId: string;
  /** 生成的项目标题 */
  title: string;
  /** 生成的项目描述 */
  description: string;
  /** 用户 ID */
  userId: string;
  /** 时间戳 (ISO 8601 格式) */
  timestamp: string;
}

/**
 * 所有项目设定生成相关的 WebSocket 消息类型联合
 */
/**
 * 分集剧本生成进度消息
 *
 * EPISODE_SCRIPT 步骤专用消息，实时推送每集剧本的生成进度
 * @see Requirements 6.4, 8.2
 */
export interface EpisodeScriptProgressMessage {
  /** 消息类型标识 */
  messageType: 'project_setting:episode_script_progress';
  /** 项目 ID */
  projectId: string;
  /** 当前集数索引 */
  episodeIndex: number;
  /** 总集数 */
  totalEpisodes: number;
  /** 当前集的生成状态 */
  status: 'started' | 'in_progress' | 'completed' | 'failed';
  /** 本次增量内容 */
  delta?: string;
  /** 完整内容（累积） */
  fullContent?: string;
  /** 错误信息（失败时） */
  errorMessage?: string;
  /** 用户 ID */
  userId: string;
  /** 时间戳 (ISO 8601 格式) */
  timestamp: string;
}

/**
 * 所有项目设定生成相关的 WebSocket 消息类型联合
 */
export type ProjectSettingGenerationMessage =
  | ProjectSettingStepStartedMessage
  | ProjectSettingStepProgressMessage
  | ProjectSettingStepCompletedMessage
  | ProjectSettingStepFailedMessage
  | ProjectMetaUpdatedMessage
  | EpisodeScriptProgressMessage;

// ============================================================================
// API 响应接口
// ============================================================================

/**
 * 自动生成状态响应
 * 
 * 用于 GET /api/user/project-setting-generation/:projectId/status 接口
 * 返回当前项目的自动生成流程状态
 * @see Requirements 5.1
 */
export interface AutoGenerationStatusResponse {
  /** 是否正在自动生成 */
  isActive: boolean;
  /** 任务 ID（如果正在生成） */
  jobId: string | null;
  /** 当前正在执行的步骤 */
  currentStep: GenerationStepType | null;
  /** 已完成的步骤列表 */
  completedSteps: GenerationStepType[];
  /** 失败的步骤（如果有） */
  failedStep: GenerationStepType | null;
  /** 错误信息（如果有） */
  errorMessage: string | null;
  /** 原始剧本创意 */
  scriptIdea: string;
  /** 小说文件 URL（小说流程） */
  novelFileUrl?: string;
  /** 分集剧本生成进度（小说流程） */
  episodeScriptProgress?: {
    totalEpisodes: number;
    completedEpisodes: number;
    currentEpisode: number | null;
    failedEpisodes: number[];
  };
}

// ============================================================================
// 请求接口
// ============================================================================

/**
 * 启动自动生成请求
 * 
 * 用于 POST /api/user/project-setting-generation/start 接口
 */
export interface StartAutoGenerationRequest {
  /** 剧本创意内容 */
  scriptIdea: string;
  /** 小说文件 URL（有值时走小说流程） */
  novelFileUrl?: string;
  /** 画面比例 */
  aspectRatio?: string;
  /** 分辨率 */
  resolution?: string;
  /** LLM 模型 ID */
  llmModelId?: string;
  /** 视觉 LLM 模型 ID */
  llmVlModelId?: string;
  /** 文生图模型 ID */
  text2imageModelId?: string;
  /** 图片编辑模型 ID */
  imageEditModelId?: string;
  /** 预设主体 ID 列表（用户从资产库选择的主体，将复制到新项目） */
  presetSubjectIds?: string[];
  /** 单个分镜最大时长（秒），如 2.5/5/7.5/10/12.5/15，不传则不限制 */
  maxShotDuration?: number;
}

/**
 * 启动自动生成响应
 */
export interface StartAutoGenerationResponse {
  /** 创建的项目 ID */
  projectId: string;
  /** 任务 ID */
  jobId: string;
}

/**
 * 重试步骤请求
 * 
 * 用于 POST /api/user/project-setting-generation/:projectId/retry 接口
 */
export interface RetryStepRequest {
  /** 要重试的步骤类型 */
  stepType: GenerationStepType;
}

/**
 * 重试步骤响应
 */
export interface RetryStepResponse {
  /** 新的任务 ID */
  jobId: string;
}

// ============================================================================
// 前端状态接口
// ============================================================================

/**
 * 自动生成状态（前端使用）
 * 
 * 用于 useAutoGeneration Hook 管理的状态
 */
export interface AutoGenerationState {
  /** 是否正在自动生成 */
  isActive: boolean;
  /** 任务 ID */
  jobId: string | null;
  /** 当前正在执行的步骤 */
  currentStep: GenerationStepType | null;
  /** 已完成的步骤列表 */
  completedSteps: GenerationStepType[];
  /** 失败的步骤 */
  failedStep: GenerationStepType | null;
  /** 错误信息 */
  errorMessage: string | null;
  /** 各步骤的流式内容（用于实时显示） */
  streamingContent: Partial<Record<GenerationStepType, string>>;
}

/**
 * 生成步骤配置
 * 
 * 用于定义步骤的显示信息
 */
export interface GenerationStepConfig {
  /** 步骤类型 */
  type: GenerationStepType;
  /** 步骤显示名称 */
  name: string;
  /** 步骤图标组件（可选） - 使用通用函数类型，避免 global 包依赖 React */
  icon?: (props: { className?: string }) => unknown;
}

/**
 * 生成步骤的完整顺序
 * 
 * 自动生成流程按此顺序执行，共 8 个步骤
 */
export const GENERATION_STEP_ORDER: GenerationStepType[] = [
  GenerationStepType.PROJECT_META,
  GenerationStepType.STORY_SYNOPSIS,
  GenerationStepType.WORLDVIEW,
  GenerationStepType.WRITING_STYLE,
  GenerationStepType.EPISODE_OUTLINE,
  GenerationStepType.ART_STYLE,
  GenerationStepType.SUBJECT,
  GenerationStepType.EPISODE_SCRIPT
];

import { I18nLabels } from './common';
import { Locale } from './locale';

/**
 * 生成步骤的显示名称映射
 */
export const GENERATION_STEP_NAMES: I18nLabels<GenerationStepType> = {
  [GenerationStepType.PROJECT_META]: { [Locale.ZH_CN]: '项目信息', [Locale.EN_US]: 'Project Info' },
  [GenerationStepType.STORY_SYNOPSIS]: { [Locale.ZH_CN]: '故事梗概', [Locale.EN_US]: 'Story Synopsis' },
  [GenerationStepType.WORLDVIEW]: { [Locale.ZH_CN]: '世界观', [Locale.EN_US]: 'Worldview' },
  [GenerationStepType.WRITING_STYLE]: { [Locale.ZH_CN]: '写作风格', [Locale.EN_US]: 'Writing Style' },
  [GenerationStepType.EPISODE_OUTLINE]: { [Locale.ZH_CN]: '分集大纲', [Locale.EN_US]: 'Episode Outline' },
  [GenerationStepType.ART_STYLE]: { [Locale.ZH_CN]: '艺术风格', [Locale.EN_US]: 'Art Style' },
  [GenerationStepType.SUBJECT]: { [Locale.ZH_CN]: '主体设定', [Locale.EN_US]: 'Subject Setting' },
  [GenerationStepType.NOVEL_PARSE]: { [Locale.ZH_CN]: '小说解析', [Locale.EN_US]: 'Novel Parse' },
  [GenerationStepType.NOVEL_SPLIT]: { [Locale.ZH_CN]: '智能切分', [Locale.EN_US]: 'Smart Split' },
  [GenerationStepType.EPISODE_SCRIPT]: { [Locale.ZH_CN]: '分集剧本', [Locale.EN_US]: 'Episode Script' },
};

/**
 * 小说流程的生成步骤顺序
 *
 * 优化后的流程:
 * 1. NOVEL_PARSE - 提取小说前3000字，LLM分析得到项目名称和故事梗概
 * 2. NOVEL_SPLIT - 小说分片，保存到剧本表
 * 3. EPISODE_OUTLINE - 根据每个分片，LLM提取得到分集大纲
 * 4. SUBJECT - 根据分集大纲，LLM提取得到主体设定
 * 5. WORLDVIEW - 世界观
 * 6. WRITING_STYLE - 写作风格
 * 7. ART_STYLE - 艺术风格
 */
export const NOVEL_GENERATION_STEP_ORDER: GenerationStepType[] = [
  GenerationStepType.NOVEL_PARSE,
  GenerationStepType.NOVEL_SPLIT,
  GenerationStepType.EPISODE_OUTLINE,
  GenerationStepType.SUBJECT,
  GenerationStepType.WORLDVIEW,
  GenerationStepType.WRITING_STYLE,
  GenerationStepType.ART_STYLE,
];

/**
 * 小说流程专用步骤名称映射（仅包含小说流程新增的步骤）
 */
export const NOVEL_GENERATION_STEP_NAMES: Partial<I18nLabels<GenerationStepType>> = {
  [GenerationStepType.NOVEL_PARSE]: { [Locale.ZH_CN]: '小说解析与切分', [Locale.EN_US]: 'Novel Parse & Split' },
};

/**
 * 总步骤数（用户可见的步骤，不包含PROJECT_META、NOVEL_PARSE、NOVEL_SPLIT）
 */
export const TOTAL_GENERATION_STEPS = 7;
